import { matchesFilter } from '@/domains/information/utils/filterCriteria';
import {
  bucketByGroup,
  countGroup,
  type CriterionGroupCounts,
} from '@/domains/information/utils/groupCriteria';

import type { ValidationReportItem } from '../api/types';

/** Filter op het Validatiescherm. Alleen UI; de data blijft ongemoeid. */
export type ValidationFilter = 'all' | 'present' | 'missing';

export type CriterionStatus = 'present' | 'inconsistent' | 'missing';

/**
 * Status van één criterium. Zelfde regel als het Informatie-scherm
 * (`matchesFilter`); inconsistent is een vorm van aanwezig.
 */
export function criterionStatus(item: ValidationReportItem): CriterionStatus {
  if (matchesFilter(item, 'missing')) return 'missing';
  return item.consistent === false ? 'inconsistent' : 'present';
}

export type ValidationCounts = {
  all: number;
  /** Aanwezig, inclusief inconsistent. */
  present: number;
  /** Aanwezig zonder tegenspraak (groen deel van de statusbalk). */
  consistent: number;
  inconsistent: number;
  missing: number;
};

/** Tellers over alle gevalideerde (= actieve) criteria; present + missing === all. */
export function countValidation(items: ValidationReportItem[]): ValidationCounts {
  const statuses = items.map(criterionStatus);
  const consistent = statuses.filter((s) => s === 'present').length;
  const inconsistent = statuses.filter((s) => s === 'inconsistent').length;
  return {
    all: items.length,
    present: consistent + inconsistent,
    consistent,
    inconsistent,
    missing: statuses.filter((s) => s === 'missing').length,
  };
}

export const percentage = (part: number, total: number): number =>
  total === 0 ? 0 : Math.round((part / total) * 100);

export type ValidationGroupView = {
  key: string;
  name: string;
  /** Over het hele onderwerp, los van filter en zoekterm. */
  counts: CriterionGroupCounts;
  /** Inconsistent eerst; leeg bij filter 'missing'. */
  found: ValidationReportItem[];
  /** Leeg bij filter 'present'. */
  missing: ValidationReportItem[];
};

/**
 * Onderwerpen met hun kolommen. `visibleItems` is wat de zoekterm overlaat;
 * onderwerpen zonder rijen voor het gekozen filter vallen weg.
 */
export function buildGroupViews(
  allItems: ValidationReportItem[],
  visibleItems: ValidationReportItem[],
  filter: ValidationFilter,
): ValidationGroupView[] {
  const visibleIds = new Set(visibleItems.map((item) => item.id));
  return bucketByGroup(allItems)
    .map((bucket) => {
      const visible = bucket.items.filter((item) => visibleIds.has(item.id));
      const withStatus = (status: CriterionStatus) =>
        visible.filter((item) => criterionStatus(item) === status);
      return {
        key: bucket.key,
        name: bucket.name,
        counts: countGroup(bucket.items),
        found:
          filter === 'missing' ? [] : [...withStatus('inconsistent'), ...withStatus('present')],
        missing: filter === 'present' ? [] : withStatus('missing'),
      };
    })
    .filter((view) => view.found.length + view.missing.length > 0);
}

/**
 * Nieuwe set dichtgeklapte onderwerpen na een accordion-wijziging. Onderwerpen
 * die nu niet zichtbaar zijn houden hun stand, zodat filteren niets openklapt.
 */
export function updateClosedKeys(
  closed: ReadonlySet<string>,
  visibleKeys: string[],
  openKeys: string[],
): Set<string> {
  const next = new Set(closed);
  for (const key of visibleKeys) {
    if (openKeys.includes(key)) next.delete(key);
    else next.add(key);
  }
  return next;
}
